import ApiService from './api';
import logger from '../utils/logger';
import {
  buildKeywordSuggestions,
  normalizeGeotagMetadata
} from '../utils/geotagging';

const pickLatLng = (candidate) => {
  if (!candidate) return { latitude: null, longitude: null };
  const latitude = Number(candidate.latitude ?? candidate.lat ?? null);
  const longitude = Number(candidate.longitude ?? candidate.lng ?? null);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return { latitude: null, longitude: null };
  }
  return { latitude, longitude };
};

const extractCoordinates = (context = {}) => {
  const { businessProfileData, businessCoordinates, fallbackLocation } = context;

  const layers = [
    businessCoordinates,
    businessProfileData?.latlng,
    businessProfileData?.location?.latlng,
    businessProfileData?.storefrontAddress,
    fallbackLocation?.latlng
  ].filter(Boolean);

  for (const layer of layers) {
    const coords = pickLatLng(layer);
    if (coords.latitude != null && coords.longitude != null) {
      return coords;
    }
  }

  return { latitude: null, longitude: null };
};

const flattenKeywords = (context = {}) => {
  const {
    businessProfileData,
    businessKeywords = [],
    category,
    existingMetadata
  } = context;

  const keywordPool = [];

  if (businessProfileData?.categories?.primaryCategory?.displayName) {
    keywordPool.push(businessProfileData.categories.primaryCategory.displayName);
  }

  if (Array.isArray(businessProfileData?.categories?.additionalCategories)) {
    keywordPool.push(
      businessProfileData.categories.additionalCategories
        .map((item) => item?.displayName)
    );
  }

  if (Array.isArray(businessProfileData?.labels)) {
    keywordPool.push(businessProfileData.labels);
  }

  if (Array.isArray(businessProfileData?.selectedKeywords)) {
    keywordPool.push(businessProfileData.selectedKeywords);
  }

  if (Array.isArray(businessProfileData?.keywords)) {
    keywordPool.push(businessProfileData.keywords);
  }

  if (Array.isArray(businessKeywords)) {
    keywordPool.push(businessKeywords);
  }

  if (category) {
    keywordPool.push([category]);
  }

  if (Array.isArray(existingMetadata?.keywords)) {
    keywordPool.push(existingMetadata.keywords);
  }

  return buildKeywordSuggestions(keywordPool).slice(0, 12);
};

const buildDescriptionPrompt = ({
  businessName,
  businessCategory,
  address,
  keywords
}) => {
  const keywordLine = keywords.length
    ? `Incorporate these focus keywords naturally: ${keywords.join(', ')}.`
    : 'Choose 2 locally relevant service keywords and include them naturally.';

  return `You are writing alt text for a Google Business Profile photo.

Business: ${businessName || 'Local business'}
Category: ${businessCategory || 'Local service'}
Address or locality: ${address || 'Not provided'}

Instructions:
- Write 1-2 short sentences (max 260 characters total) describing what the photo likely shows.
- Mention the business name once if available.
- Highlight local context (neighbourhood or city) when the address is provided.
- Focus on benefits or ambience (people, activity, products, ambience) relevant to the business.
- ${keywordLine}
- Use the uploaded photo (if provided) to mention what is visually present (people, signage, setting, colours).
- Do not use hashtags, emojis, or marketing fluff.
- Return only the plain text description.`;
};

const safeTrim = (value) => (typeof value === 'string' ? value.trim() : '');

const extractVisionPayload = (text = '') => {
  if (!text) return null;
  const trimmed = text.trim().replace(/```json/gi, '').replace(/```/g, '').trim();
  const match = trimmed.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]);
  } catch (error) {
    logger.warn('[GeotagService] Unable to parse vision JSON payload', { snippet: match[0] });
    return null;
  }
};

const selectVisionDescription = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return '';
  const candidates = [
    payload.description,
    payload.alt_text,
    payload.altText,
    payload.caption,
    payload.summary,
    payload.post
  ];

  for (const candidate of candidates) {
    const trimmed = safeTrim(candidate);
    if (trimmed) {
      return trimmed;
    }
  }

  return '';
};

const GeotagService = {
  buildDefaultMetadata(context = {}) {
    const coordinates = extractCoordinates(context);
    const keywords = flattenKeywords(context);

    const merged = normalizeGeotagMetadata({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      keywords,
      description: context?.existingMetadata?.description || ''
    });

    return merged;
  },

  async generateDescription(context = {}) {
    const keywords = flattenKeywords(context);
    const coords = extractCoordinates(context);
    const prompt = buildDescriptionPrompt({
      businessName: context.businessName,
      businessCategory: context.category,
      address: context.address,
      keywords
    });

    const imageBase64 = safeTrim(context.imageBase64 || context.imageDataUrl || '');

    if (imageBase64) {
      try {
        const response = await ApiService.post('/customer/ai/describe-image', {
          imageBase64,
          prompt,
          context: {
            business_name: context.businessName
              || context.businessProfileData?.title
              || context.businessProfileData?.name
              || '',
            category: context.category
              || context.businessProfileData?.category
              || context.businessProfileData?.categories?.primaryCategory?.displayName
              || '',
            city: context.address
              || context.businessProfileData?.storefrontAddress?.locality
              || context.businessProfileData?.storefrontAddress?.postalCode
              || '',
            neighborhood: context.businessProfileData?.storefrontAddress?.subLocality
              || context.businessProfileData?.storefrontAddress?.addressLines?.[0]
              || '',
            keywords,
            latitude: coords.latitude,
            longitude: coords.longitude
          }
        });

        const payload = response?.data || {};
        const structured = payload.structured || extractVisionPayload(payload.text || payload.raw || '');

        const structuredDescription = selectVisionDescription(structured);
        if (structuredDescription) {
          return structuredDescription.replace(/\s+/g, ' ').slice(0, 260);
        }

        const textCandidate = safeTrim(payload.text || payload.raw || '');
        if (textCandidate) {
          const parsed = extractVisionPayload(textCandidate);
          const parsedDescription = selectVisionDescription(parsed);
          if (parsedDescription) {
            return parsedDescription.replace(/\s+/g, ' ').slice(0, 260);
          }

          if (!textCandidate.trim().startsWith('{')) {
            return textCandidate.replace(/\s+/g, ' ').slice(0, 260);
          }
        }
      } catch (visionError) {
        logger.warn('[GeotagService] Vision description failed, falling back to text model:', visionError);
      }
    }

    try {
      const response = await ApiService.post('/customer/ai/generate', {
        prompt,
        model: 'gemini-1.5-flash',
        temperature: 0.55,
        maxOutputTokens: 256,
        context: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          keywords
        }
      });

      const raw = response?.data?.text || response?.text || '';
      const cleaned = safeTrim(raw)
        .replace(/^"|"$/g, '')
        .replace(/\s+/g, ' ');

      if (!cleaned) {
        throw new Error('Empty description response');
      }

      return cleaned.slice(0, 260);
    } catch (error) {
      logger.error('[GeotagService] Failed to generate AI description:', error);
      throw error;
    }
  },

  mergeMetadata(base = {}, overrides = {}) {
    const normalizedBase = normalizeGeotagMetadata(base);
    const normalizedOverrides = normalizeGeotagMetadata(overrides);

    return normalizeGeotagMetadata({
      latitude: normalizedOverrides.latitude ?? normalizedBase.latitude,
      longitude: normalizedOverrides.longitude ?? normalizedBase.longitude,
      keywords: normalizedOverrides.keywords?.length
        ? normalizedOverrides.keywords
        : normalizedBase.keywords,
      description: normalizedOverrides.description || normalizedBase.description
    });
  }
};

export default GeotagService;
